import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FacturacionMasivaService } from '../../services/facturacion-masiva/facturacion-masiva.service';
import swal2 from 'sweetalert2';

// Página principal de Facturación Masiva: carga de archivo Excel y listado de lotes. 23/07/2026
@Component({
  selector: 'app-facturacion-masiva',
  templateUrl: './facturacion-masiva.component.html',
  styleUrls: ['./facturacion-masiva.component.css']
})
export class FacturacionMasivaComponent implements OnInit, OnDestroy {

  // Archivo seleccionado
  selectedFile: File = null;
  isDragging = false;

  // Lote en proceso (polling)
  activeBatch: any = null;
  pollingInterval: any = null;

  // Historial de lotes
  batches: any[] = [];
  loadingBatches = false;

  // Paginación y filtro
  searchTerm = '';
  currentPage = 1;
  itemsPerPage = 10;
  filaSeleccionada: number | null = null;

  // Límites
  maxFileSize = 10; // MB
  allowedExtension = '.xlsx';

  constructor(
    private facturacionService: FacturacionMasivaService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadBatches();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  // Carga el historial de lotes del usuario. 23/07/2026
  loadBatches() {
    this.loadingBatches = true;
    this.facturacionService.getBatches().subscribe(
      (resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        this.batches = Array.isArray(data) ? data : [];
        this.loadingBatches = false;
      },
      (err) => {
        console.error('Error al cargar lotes', err);
        this.batches = [];
        this.loadingBatches = false;
      }
    );
  }

  // Maneja el evento de drag over para el área de drop. 23/07/2026
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  // Maneja el evento de drag leave. 23/07/2026
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  // Maneja el drop del archivo. 23/07/2026
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      this.validateAndSetFile(files[0]);
    }
  }

  // Maneja selección de archivo por input. 23/07/2026
  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.validateAndSetFile(file);
    }
  }

  // Valida extensión y tamaño del archivo antes de asignarlo. 23/07/2026
  validateAndSetFile(file: File) {
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (extension !== this.allowedExtension) {
      swal2.fire('Formato no válido', 'Solo se acepta archivos con extensión .xlsx', 'error');
      this.selectedFile = null;
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > this.maxFileSize) {
      swal2.fire('Archivo muy grande', `El archivo excede el máximo de ${this.maxFileSize} MB`, 'error');
      this.selectedFile = null;
      return;
    }

    this.selectedFile = file;
  }

  // Remueve el archivo seleccionado. 23/07/2026
  removeFile() {
    this.selectedFile = null;
  }

  // Sube el archivo al backend para procesamiento. 23/07/2026
  uploadFile() {
    if (!this.selectedFile) {
      swal2.fire('Sin archivo', 'Selecciona un archivo .xlsx para subir', 'warning');
      return;
    }

    swal2.fire({ title: 'Subiendo archivo...', allowOutsideClick: false });
    swal2.showLoading();

    this.facturacionService.uploadLayout(this.selectedFile).subscribe(
      (resp: any) => {
        const payload = resp && resp.data ? resp.data : resp;
        const batchId = payload ? (payload.batch_id || payload.batchId || payload.id) : null;
        if (batchId) {
          swal2.fire('Lote creado', `El lote #${batchId} fue creado exitosamente. El procesamiento se realizará en segundo plano.`, 'success');
          this.selectedFile = null;
          this.activeBatch = { id: batchId, status: payload.status || 'PENDIENTE' };
          this.startPolling(batchId);
          this.loadBatches();
        } else {
          console.log('Upload resp sin batch_id:', resp);
          swal2.fire('Respuesta inesperada', 'El servidor no devolvió un ID de lote válido.', 'warning');
        }
      },
      (err) => {
        const errorMsg = (err.error && typeof err.error === 'object' && err.error.error)
          ? err.error.error
          : 'Error de conexión con el servidor';
        swal2.fire('Error al subir', errorMsg, 'error');
      }
    );
  }

  // Inicia polling del lote activo cada 3 segundos. 23/07/2026
  startPolling(batchId: number) {
    this.stopPolling();
    this.pollingInterval = setInterval(() => {
      this.facturacionService.getBatch(batchId).subscribe(
        (resp: any) => {
          const batch = resp && resp.data ? resp.data : resp;
          if (batch && batch.id) {
            this.activeBatch = batch;
          }
          const status = batch ? batch.status : null;
          if (status && status !== 'PENDIENTE' && status !== 'PROCESANDO') {
            this.stopPolling();
            this.loadBatches();
          }
        },
        (err) => {
          console.error('Error en polling', err);
          // No borrar activeBatch en error, solo dejar de hacer polling
          this.stopPolling();
        }
      );
    }, 3000);
  }

  // Detiene el polling activo. 23/07/2026
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  // Navega al detalle de un lote. 23/07/2026
  verDetalleLote(batchId: number) {
    this.router.navigate(['/facturacionmasiva/lote', batchId]);
  }

  // Retorna clase CSS según el estatus del lote. 23/07/2026
  getStatusClass(status: string): string {
    switch (status) {
      case 'COMPLETADO': return 'badge-success';
      case 'COMPLETADO_CON_ERRORES': return 'badge-warning';
      case 'ERROR': return 'badge-danger';
      case 'PROCESANDO': return 'badge-info';
      case 'PENDIENTE': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  }

  // Retorna texto legible del estatus. 23/07/2026
  getStatusLabel(status: string): string {
    switch (status) {
      case 'COMPLETADO': return 'Completado';
      case 'COMPLETADO_CON_ERRORES': return 'Con errores';
      case 'ERROR': return 'Error';
      case 'PROCESANDO': return 'Procesando';
      case 'PENDIENTE': return 'Pendiente';
      default: return status;
    }
  }

  // --- Paginación y filtro del historial. 23/07/2026 ---

  get filteredData(): any[] {
    if (!this.searchTerm) {
      return this.batches;
    }
    const term = this.searchTerm.toLowerCase();
    return this.batches.filter(lote =>
      (lote.file_name && lote.file_name.toLowerCase().includes(term)) ||
      (lote.status && lote.status.toLowerCase().includes(term)) ||
      (lote.id && lote.id.toString().includes(term))
    );
  }

  get dataToShow(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredData.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredData.length / this.itemsPerPage);
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  resetPage() {
    this.currentPage = 1;
    this.filaSeleccionada = null;
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  seleccionarFila(index: number) {
    this.filaSeleccionada = index + (this.currentPage - 1) * this.itemsPerPage;
  }

}
